"use strict";
/**
 * Additional Markets Modal Handler
 *
 * Manages the four-step modal flow for assigning additional markets
 * to buy buttons in any order-button layout:
 *
 * Step 1: Select venue (Kalshi / PM-US)
 * Step 2: Select series / league via preset buttons or manual search
 * Step 3: Browse events, expand to see markets, multi-select with checkboxes
 * Step 4: Configure side, dollar amount, manual price, and select buttons
 *         (grid rendered from the active layout; see
 *         additional-markets-button-grid), then confirm — this step creates
 *         the assignment.
 *
 * There is deliberately no confirmation step. It restated venue, markets,
 * side, amount, price and buttons, every one of which is still on screen and
 * still editable one row above the confirm button; assignments also stay
 * editable and removable from the settings list.
 *
 * Also supports a legacy flow (used by tests) where venue is chosen
 * via a dropdown in step 1 and a single market is selected by clicking.
 *
 * @module handlers/modals/additional-markets-modal
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.showAdditionalMarketsModal = showAdditionalMarketsModal;
exports.hideAdditionalMarketsModal = hideAdditionalMarketsModal;
exports.openBatModal = openBatModal;
exports.openPitchModal = openPitchModal;
exports.openTotalModal = openTotalModal;
exports.openEditModal = openEditModal;
exports.loadPmUsSports = loadPmUsSports;
exports.setupAdditionalMarketsModal = setupAdditionalMarketsModal;
const state_1 = require("../../state");
const kalshi_1 = require("../../api/kalshi");
const additional_markets_service_1 = require("../../services/additional-markets-service");
const additional_markets_button_grid_1 = require("./additional-markets-button-grid");
const permissions_1 = require("../../permissions");
const market_categories_1 = require("../../markets/market-categories");
const pm_us_adapters_1 = require("./shared/pm-us-adapters");
const pm_us_default_league_1 = require("./shared/pm-us-default-league");
const kalshi_series_presets_1 = require("../../markets/kalshi-series-presets");
const novig_1 = require("../../api/novig");
const cache_1 = require("../../markets/cache");
const constants_1 = require("../../markets/constants");
const parallel_markets_service_1 = require("../../services/parallel-markets-service");
const market_sides_1 = require("./shared/market-sides");
const novig_adapters_1 = require("./shared/novig-adapters");
const additional_markets_ui_1 = require("./additional-markets-ui");
const polymarket_us_1 = require("../../api/polymarket-us");
const DEFAULT_ASSIGNMENT_DOLLAR_AMOUNT = 1000;
const DEFAULT_ASSIGNMENT_MANUAL_PRICE = 99;
/** Buttons pre-checked by default when entering via the bat button. */
const BAT_DEFAULT_BUTTONS = new Set([
    "single",
    "double",
    "triple",
    "hr",
    "ext_off",
]);
/** Buttons pre-checked by default when entering via the pitch button. */
const PITCH_DEFAULT_BUTTONS = new Set(["ext_def"]);
/** Buttons pre-checked by default when entering via the total button. */
const TOTAL_DEFAULT_BUTTONS = new Set([
    "misc_scaled_off",
    "ext_off",
    "single",
    "double",
    "triple",
    "hr",
]);
/**
 * Mapping of source tags to their default button sets.
 * Used by advanceToStep4 to pre-select buttons based on modal source.
 */
const SOURCE_DEFAULT_BUTTONS = {
    bat: BAT_DEFAULT_BUTTONS,
    pitch: PITCH_DEFAULT_BUTTONS,
    total: TOTAL_DEFAULT_BUTTONS,
};
let modalState = {
    venue: "kalshi",
    events: [],
    expandedEventTicker: null,
    expandedMarkets: [],
    selectedMarket: null,
    selectedEventTicker: null,
    selectedMarkets: [],
    selectedEventTickers: new Map(),
    pendingAssignmentConfig: null,
    pmUsSportsLoaded: false,
    source: undefined,
    marketFilterTerm: "",
    marketCategoryFilter: null,
    marketFilterSelectionStart: null,
    marketFilterSelectionEnd: null,
    editingAssignmentId: null,
};
function resetModalState() {
    modalState = {
        venue: "kalshi",
        events: [],
        expandedEventTicker: null,
        expandedMarkets: [],
        selectedMarket: null,
        selectedEventTicker: null,
        selectedMarkets: [],
        selectedEventTickers: new Map(),
        pendingAssignmentConfig: null,
        pmUsSportsLoaded: modalState.pmUsSportsLoaded,
        source: undefined,
        marketFilterTerm: "",
        marketCategoryFilter: null,
        marketFilterSelectionStart: null,
        marketFilterSelectionEnd: null,
        editingAssignmentId: null,
    };
}
// ---------------------------------------------------------------------------
// Modal show/hide
// ---------------------------------------------------------------------------
function showAdditionalMarketsModal() {
    const modal = document.getElementById("additional-markets-modal");
    if (modal) {
        modal.classList.remove("hidden");
        resetModalState();
        applyVenueUI();
        showStep(1);
    }
}
function hideAdditionalMarketsModal() {
    const modal = document.getElementById("additional-markets-modal");
    if (modal) {
        modal.classList.add("hidden");
        resetModalState();
    }
}
/**
 * Open the additional markets modal in a preset "shortcut" mode.
 * Clears prior assignments with the given source, pre-selects Kalshi venue
 * and the given series, then jumps straight to step 3 (market selection).
 */
function openPresetModal(source, seriesTicker) {
    (0, additional_markets_service_1.removeAssignmentsBySource)(source);
    (0, additional_markets_ui_1.updateAdditionalMarketBadges)();
    (0, additional_markets_ui_1.updateSettingsInfoAdditionalMarkets)();
    const modal = document.getElementById("additional-markets-modal");
    if (!modal)
        return;
    modal.classList.remove("hidden");
    resetModalState();
    modalState.venue = "kalshi";
    modalState.source = source;
    showStep(3);
    searchEvents(seriesTicker);
}
/**
 * Open the additional markets modal in "bat" mode:
 * - Clears prior bat-sourced assignments
 * - Pre-selects Kalshi venue + KXMLBHIT series
 * - Jumps straight to step 3 (market selection)
 */
function openBatModal() {
    openPresetModal("bat", "KXMLBHIT");
}
/**
 * Open the additional markets modal in "pitch" mode:
 * - Clears prior pitch-sourced assignments
 * - Pre-selects Kalshi venue + KXMLBKS series
 * - Jumps straight to step 3 (market selection)
 */
function openPitchModal() {
    openPresetModal("pitch", "KXMLBKS");
}
/**
 * Open the additional markets modal in "total" mode:
 * - Clears prior total-sourced assignments
 * - Pre-selects Kalshi venue + KXMLBTOTAL series
 * - Jumps straight to step 3 (market selection)
 */
function openTotalModal() {
    openPresetModal("total", "KXMLBTOTAL");
}
/**
 * Open the additional markets modal in "edit" mode for an existing assignment.
 * Jumps directly to Step 4 (Configure Assignment) pre-populated with the
 * assignment's current side, dollar amount, manual price, and button selections.
 * On confirmation, the existing assignment is updated in-place rather than a
 * new assignment being created.
 *
 * @param assignmentId - The ID of the assignment to edit.
 */
function openEditModal(assignmentId) {
    const assignments = (0, additional_markets_service_1.getAdditionalAssignments)();
    const assignment = assignments.find((a) => a.id === assignmentId);
    if (!assignment)
        return;
    const modal = document.getElementById("additional-markets-modal");
    if (!modal)
        return;
    modal.classList.remove("hidden");
    resetModalState();
    modalState.editingAssignmentId = assignmentId;
    modalState.venue = assignment.venue ?? "kalshi";
    // Show the selected market info in the Step 4 header
    const marketInfo = document.getElementById("am-selected-market-info");
    if (marketInfo) {
        const displayTitle = assignment.marketTitle && assignment.marketTitle.length < 50
            ? assignment.marketTitle
            : assignment.marketTicker;
        marketInfo.textContent = displayTitle;
    }
    // Pre-populate form fields with current assignment values
    const sideSelect = document.getElementById("am-side-select");
    const dollarInput = document.getElementById("am-dollar-input");
    const priceInput = document.getElementById("am-price-input");
    if (sideSelect)
        sideSelect.value = assignment.side;
    // No market rows here to name the sides from; never leave the last market's names.
    labelSideSelect([]);
    if (dollarInput)
        dollarInput.value = String(assignment.dollarAmount);
    if (priceInput)
        priceInput.value = String(assignment.manualPrice);
    // Rebuild the button grid with the assignment's buttons pre-checked.
    // Buttons outside the active layout are rendered too (dimmed, under a
    // divider) so saving the edit cannot silently drop dormant assignments.
    (0, additional_markets_button_grid_1.renderButtonGrid)(new Set(assignment.buttons), assignment.buttons);
    showStep(4);
}
function showStep(step) {
    const steps = document.querySelectorAll(".am-step");
    steps.forEach((s) => s.classList.add("hidden"));
    const target = document.getElementById(`am-step-${step}`);
    if (target)
        target.classList.remove("hidden");
}
// ---------------------------------------------------------------------------
// Venue switching
// ---------------------------------------------------------------------------
function applyVenueUI() {
    const kalshiRow = document.getElementById("am-kalshi-search-row");
    const pmusRow = document.getElementById("am-pmus-search-row");
    if (modalState.venue === "pm-us") {
        kalshiRow?.classList.add("hidden");
        pmusRow?.classList.remove("hidden");
        if (!modalState.pmUsSportsLoaded) {
            loadPmUsSports();
        }
    }
    else {
        kalshiRow?.classList.remove("hidden");
        pmusRow?.classList.add("hidden");
    }
}
// ---------------------------------------------------------------------------
// PM-US: response normalisation helpers
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// PM-US: populate sports dropdown
// ---------------------------------------------------------------------------
/** Fetch sports from PM-US API and populate the `<select>`. */
/**
 * Fetch + normalise the PM-US sports list, the single source of truth for
 * every consumer here (legacy dropdown, step-2 presets, default-league
 * validation). Never throws — a failure surfaces as `error: true` so callers
 * can render their own message without duplicating the try/catch.
 */
async function loadPmUsSportsList() {
    try {
        const response = await (0, polymarket_us_1.listPolymarketUsSports)();
        return {
            sports: (0, pm_us_adapters_1.normalizeSportsResponse)(response.data),
            error: false,
        };
    }
    catch (err) {
        console.error("[AdditionalMarkets] Failed to load PM-US sports:", err);
        return { sports: [], error: true };
    }
}
async function loadPmUsSports() {
    const sportSelect = document.getElementById("am-sport-select");
    if (!sportSelect)
        return;
    sportSelect.innerHTML = '<option value="">Loading sports...</option>';
    sportSelect.disabled = true;
    const { sports, error } = await loadPmUsSportsList();
    if (error) {
        sportSelect.innerHTML =
            '<option value="">Failed to load sports</option>';
        sportSelect.disabled = true;
        return;
    }
    sportSelect.innerHTML = "";
    if (sports.length === 0) {
        sportSelect.innerHTML = '<option value="">No sports available</option>';
        sportSelect.disabled = true;
        return;
    }
    for (const s of sports) {
        const option = document.createElement("option");
        option.value = (0, pm_us_adapters_1.pmUsSportEntrySlug)(s);
        option.textContent = String(s.sport ?? s.name ?? s.slug ?? "Unknown");
        sportSelect.appendChild(option);
    }
    sportSelect.disabled = false;
    modalState.pmUsSportsLoaded = true;
}
// ---------------------------------------------------------------------------
// Step 1: Event/Market Search
// ---------------------------------------------------------------------------
async function searchEvents(seriesTicker) {
    const resultsContainer = document.getElementById("am-event-results");
    const loadingEl = document.getElementById("am-search-loading");
    if (!resultsContainer)
        return;
    if (!seriesTicker.trim()) {
        resultsContainer.innerHTML =
            '<p class="am-hint">Enter a series ticker to search events.</p>';
        return;
    }
    if (loadingEl)
        loadingEl.classList.remove("hidden");
    resultsContainer.innerHTML = "";
    try {
        const response = await (0, kalshi_1.getEvents)(50, seriesTicker, undefined, false, "open");
        modalState.events = response.events || [];
        if (modalState.events.length === 0) {
            resultsContainer.innerHTML =
                '<p class="am-hint">No open events found for this series.</p>';
        }
        else {
            renderEventList(resultsContainer);
        }
    }
    catch (err) {
        console.error("[AdditionalMarkets] Failed to fetch events:", err);
        resultsContainer.innerHTML =
            '<p class="am-hint am-error">Failed to load events. Check series ticker.</p>';
    }
    finally {
        if (loadingEl)
            loadingEl.classList.add("hidden");
    }
}
/** Search events via PM-US API using the selected sport (tagSlug). */
async function searchPmUsEvents(tagSlug) {
    const resultsContainer = document.getElementById("am-event-results");
    const loadingEl = document.getElementById("am-search-loading");
    if (!resultsContainer)
        return;
    if (!tagSlug) {
        resultsContainer.innerHTML =
            '<p class="am-hint">Select a sport to search events.</p>';
        return;
    }
    if (loadingEl)
        loadingEl.classList.remove("hidden");
    resultsContainer.innerHTML = "";
    try {
        const response = await (0, polymarket_us_1.listPolymarketUsEvents)({
            limit: pm_us_adapters_1.PM_US_EVENTS_LIMIT,
            active: true,
            closed: false,
            tagSlug,
            gamesOnly: true,
        });
        // Normalise the response data (may be array or {events: []})
        const rawEvents = (0, pm_us_adapters_1.normalizePmUsEventsResponse)(response.data);
        // Cache raw events so we can look up markets on expand
        // Convert to Kalshi Event shape for the shared render path
        modalState.events = rawEvents.map(pm_us_adapters_1.pmUsEventToEvent);
        if (modalState.events.length === 0) {
            resultsContainer.innerHTML =
                '<p class="am-hint">No open events found for this sport.</p>';
        }
        else {
            renderEventList(resultsContainer);
        }
    }
    catch (err) {
        console.error("[AdditionalMarkets] Failed to fetch PM-US events:", err);
        resultsContainer.innerHTML =
            '<p class="am-hint am-error">Failed to load PM-US events.</p>';
    }
    finally {
        if (loadingEl)
            loadingEl.classList.add("hidden");
    }
}
/** A Novig league's open events (ai/NOVIG_VENUE_PLAN.md phase 9.4). */
async function searchNovigEvents(league) {
    const resultsContainer = document.getElementById("am-event-results");
    const loadingEl = document.getElementById("am-search-loading");
    if (!resultsContainer)
        return;
    if (!league) {
        resultsContainer.innerHTML = '<p class="am-hint">Select a league to search events.</p>';
        return;
    }
    if (loadingEl)
        loadingEl.classList.remove("hidden");
    resultsContainer.innerHTML = "";
    try {
        modalState.events = (await (0, novig_1.listNovigEvents)(league)).map(novig_adapters_1.novigEventToEvent);
        if (modalState.events.length === 0) {
            resultsContainer.innerHTML = '<p class="am-hint">No open events found for this league.</p>';
        }
        else {
            renderEventList(resultsContainer);
        }
    }
    catch (err) {
        console.error("[AdditionalMarkets] Failed to fetch Novig events:", err);
        resultsContainer.innerHTML = '<p class="am-hint am-error">Failed to load Novig events.</p>';
    }
    finally {
        if (loadingEl)
            loadingEl.classList.add("hidden");
    }
}
/**
 * Enter the Novig branch from the step-1 venue button: its leagues as step-2
 * presets, or straight to step 3 on the league being browsed when the primary
 * is a Novig market.
 */
async function enterNovigVenue() {
    modalState.venue = "novig";
    modalState.selectedMarkets = [];
    modalState.selectedEventTickers = new Map();
    updateMarketNextButton();
    const step2Input = document.getElementById("am-step2-search-input");
    if (step2Input) {
        step2Input.placeholder = "Or type a league (e.g. NFL)...";
        step2Input.value = "";
    }
    showStep(2);
    const grid = document.getElementById("am-preset-grid");
    if (grid)
        grid.innerHTML = '<p class="am-hint">Loading leagues...</p>';
    let leagues;
    try {
        leagues = await (0, novig_1.listNovigLeagues)();
    }
    catch (err) {
        console.error("[AdditionalMarkets] Failed to load Novig leagues:", err);
        if (grid)
            grid.innerHTML = '<p class="am-hint am-error">Failed to load Novig leagues.</p>';
        return;
    }
    if (grid) {
        grid.innerHTML = "";
        for (const league of leagues) {
            const btn = document.createElement("button");
            btn.className = "am-preset-btn";
            btn.type = "button";
            btn.textContent = (0, constants_1.novigLeagueLabel)(league);
            btn.addEventListener("click", () => {
                modalState.selectedMarkets = [];
                modalState.selectedEventTickers = new Map();
                updateMarketNextButton();
                showStep(3);
                void searchNovigEvents(league);
            });
            grid.appendChild(btn);
        }
    }
    const current = (0, state_1.getState)().dataSource === "novig" ? (0, cache_1.getCurrentCategory)() : "";
    if (current && leagues.includes(current)) {
        showStep(3);
        void searchNovigEvents(current);
    }
}
/**
 * Step 4's side picker names each side when every selected market names them
 * the same way (one market, usually); otherwise plain YES / NO.
 */
function labelSideSelect(markets) {
    const select = document.getElementById("am-side-select");
    if (!select)
        return;
    const pairs = markets.map((m) => (0, parallel_markets_service_1.legSideLabels)((0, market_sides_1.marketSideNames)(m)).join("\u0000"));
    const shared = pairs.length > 0 && pairs.every((p) => p === pairs[0]);
    const [yes, no] = shared ? pairs[0].split("\u0000") : ["YES", "NO"];
    for (const option of Array.from(select.options)) {
        if (option.value === "yes")
            option.textContent = yes;
        if (option.value === "no")
            option.textContent = no;
    }
}
function renderEventList(container) {
    container.innerHTML = "";
    const eventsToRender = getFilteredEvents(modalState.events);
    for (const event of eventsToRender) {
        const item = document.createElement("div");
        item.className = "am-event-item";
        item.dataset.eventTicker = event.event_ticker;
        const header = document.createElement("div");
        header.className = "am-event-header";
        header.innerHTML = `
			<span class="am-event-main">
				<span class="am-event-title">${escapeHtml(event.title)}</span>
				<span class="am-event-id">${escapeHtml(modalState.venue === "novig" ? (event.sub_title ?? "") : event.event_ticker)}</span>
			</span>
			<span class="am-event-chevron">${modalState.expandedEventTicker === event.event_ticker ? "▼" : "▶"}</span>
		`;
        header.addEventListener("click", () => toggleEventExpand(event.event_ticker, item));
        item.appendChild(header);
        // If this event is expanded, show its markets
        if (modalState.expandedEventTicker === event.event_ticker) {
            const marketsDiv = document.createElement("div");
            marketsDiv.className = "am-markets-list";
            renderMarketsForEvent(marketsDiv, event.event_ticker);
            item.appendChild(marketsDiv);
        }
        container.appendChild(item);
    }
}
function getFilterTerm() {
    const filterInput = document.getElementById("am-filter-input");
    return filterInput?.value.trim().toLowerCase() ?? "";
}
function getFilteredEvents(events) {
    const term = getFilterTerm();
    if (!term) {
        return events.sort((a, b) => (a.last_updated_ts ?? "").localeCompare(b.last_updated_ts ?? ""));
    }
    return events
        .filter((event) => event.title.toLowerCase().includes(term) ||
        event.event_ticker.toLowerCase().includes(term))
        .sort((a, b) => (a.last_updated_ts ?? "").localeCompare(b.last_updated_ts ?? "")); // sort by last updated timestamp ascending (oldest first)
}
/** Compute the display title for a market (short title if <50 chars, else ticker). */
function getDisplayTitle(market) {
    if (market.title && market.title.length < 50)
        return market.title;
    // A Novig ticker is a UUID: a cut title reads better than it.
    if (modalState.venue === "novig")
        return `${market.title.slice(0, 47)}...`;
    return market.ticker || market.title;
}
/**
 * Filter expanded markets by the active category chip (if any) AND the text
 * term (`modalState.marketFilterTerm`, matched against title + subtitle).
 */
function getFilteredMarkets(markets) {
    const category = modalState.marketCategoryFilter;
    const term = modalState.marketFilterTerm.trim().toLowerCase();
    return markets.filter((m) => {
        if (category && (0, market_categories_1.categoryKeyForMarket)(m) !== category)
            return false;
        if (!term)
            return true;
        const title = getDisplayTitle(m).toLowerCase();
        const subtitle = (m.yes_sub_title || m.subtitle || "").toLowerCase();
        return title.includes(term) || subtitle.includes(term);
    });
}
async function toggleEventExpand(eventTicker, itemEl) {
    const resultsContainer = document.getElementById("am-event-results");
    if (modalState.expandedEventTicker === eventTicker) {
        // Collapse
        modalState.expandedEventTicker = null;
        modalState.expandedMarkets = [];
        modalState.marketFilterTerm = "";
        modalState.marketCategoryFilter = null;
        modalState.marketFilterSelectionStart = null;
        modalState.marketFilterSelectionEnd = null;
        if (resultsContainer)
            renderEventList(resultsContainer);
        return;
    }
    // Expand: fetch markets for this event
    modalState.expandedEventTicker = eventTicker;
    modalState.expandedMarkets = [];
    modalState.marketFilterTerm = "";
    modalState.marketCategoryFilter = null;
    modalState.marketFilterSelectionStart = null;
    modalState.marketFilterSelectionEnd = null;
    if (resultsContainer)
        renderEventList(resultsContainer);
    try {
        let markets;
        if (modalState.venue === "pm-us") {
            // PM-US markets are loaded per event, same as Kalshi: the event
            // list deliberately arrives without them.
            const response = await (0, polymarket_us_1.getPolymarketUsEventMarkets)(eventTicker);
            markets = (response.data?.markets ?? []).map((m) => (0, pm_us_adapters_1.pmUsMarketToMarketFromEvent)(m, eventTicker));
        }
        else if (modalState.venue === "novig") {
            markets = (await (0, novig_1.getNovigEventMarkets)(eventTicker)).map((m) => (0, novig_adapters_1.novigMarketToMarketFromEvent)(m, eventTicker));
        }
        else {
            const response = await (0, kalshi_1.getMarketsByEvent)(eventTicker);
            markets = response.markets || [];
        }
        // Discard a late response for an event the operator already collapsed
        // or navigated away from.
        if (modalState.expandedEventTicker !== eventTicker)
            return;
        modalState.expandedMarkets = markets;
        if (resultsContainer)
            renderEventList(resultsContainer);
    }
    catch (err) {
        console.error("[AdditionalMarkets] Failed to fetch markets for event:", err);
        if (modalState.expandedEventTicker !== eventTicker)
            return;
        modalState.expandedMarkets = [];
        if (resultsContainer)
            renderEventList(resultsContainer);
    }
}
/**
 * Render the horizontal, scrollable category-filter chip row into `container`.
 *
 * Venue-blind: the row only appears when the expanded event's markets span ≥2
 * categories, so it shows for a PM-US MLB event (props/totals/spreads/innings…)
 * and stays hidden for a homogeneous Kalshi series. Single-select — one chip or
 * "All" — combining with the text filter as AND (see getFilteredMarkets).
 */
function renderMarketCategoryRow(container) {
    const categories = (0, market_categories_1.deriveMarketCategories)(modalState.expandedMarkets);
    if (categories.length < 2)
        return;
    const row = document.createElement("div");
    row.className = "am-market-category-row";
    const makeChip = (label, key, count) => {
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "am-category-chip";
        if (modalState.marketCategoryFilter === key) {
            chip.classList.add("am-category-chip--active");
        }
        chip.textContent = count === null ? label : `${label} (${count})`;
        chip.addEventListener("click", () => {
            if (modalState.marketCategoryFilter === key)
                return;
            modalState.marketCategoryFilter = key;
            // Move the active highlight in place and re-filter only the market
            // rows below. The chip row's own DOM is untouched, so its horizontal
            // scroll position is preserved with no save/restore dance.
            row.querySelectorAll(".am-category-chip").forEach((c) => c.classList.remove("am-category-chip--active"));
            chip.classList.add("am-category-chip--active");
            const itemsContainer = document.querySelector(".am-market-items");
            if (itemsContainer && modalState.expandedEventTicker) {
                renderMarketItems(itemsContainer, modalState.expandedEventTicker);
            }
        });
        return chip;
    };
    row.appendChild(makeChip("All", null, null));
    for (const cat of categories) {
        row.appendChild(makeChip(cat.label, cat.key, cat.count));
    }
    container.appendChild(row);
}
function renderMarketsForEvent(container, eventTicker) {
    // Detect multi-select mode (production DOM has am-step-4)
    const isMultiSelect = !!document.getElementById("am-step-4");
    if (modalState.expandedMarkets.length === 0) {
        container.innerHTML = '<p class="am-hint">Loading markets...</p>';
        return;
    }
    // Add market search/filter input at the top of the expanded market list
    if (isMultiSelect) {
        const filterRow = document.createElement("div");
        filterRow.className = "am-market-filter-row";
        const filterInput = document.createElement("input");
        filterInput.type = "text";
        filterInput.className = "am-input am-market-filter-input";
        filterInput.placeholder = "Filter markets...";
        filterInput.autocomplete = "off";
        filterInput.value = modalState.marketFilterTerm;
        filterInput.addEventListener("input", () => {
            modalState.marketFilterTerm = filterInput.value;
            modalState.marketFilterSelectionStart = filterInput.selectionStart;
            modalState.marketFilterSelectionEnd = filterInput.selectionEnd;
            const resultsContainer = document.getElementById("am-event-results");
            if (resultsContainer) {
                renderEventList(resultsContainer);
                const replacementInput = resultsContainer.querySelector(".am-market-filter-input");
                if (replacementInput) {
                    const selectionStart = modalState.marketFilterSelectionStart ??
                        modalState.marketFilterTerm.length;
                    const selectionEnd = modalState.marketFilterSelectionEnd ??
                        modalState.marketFilterTerm.length;
                    replacementInput.focus();
                    // Clamp to the replacement value length so we never restore
                    // an out-of-bounds caret if the rendered value changes.
                    replacementInput.setSelectionRange(Math.min(selectionStart, replacementInput.value.length), Math.min(selectionEnd, replacementInput.value.length));
                }
            }
        });
        filterRow.appendChild(filterInput);
        container.appendChild(filterRow);
        // Category-filter chips, directly under the text filter. Auto-hides
        // when the event spans <2 categories (e.g. any Kalshi series).
        renderMarketCategoryRow(container);
    }
    // Market rows live in their own container so a chip tap (or any future
    // in-place filter) can re-render just them, leaving the filter/chip rows —
    // and the chip row's scroll position — above untouched.
    const itemsContainer = document.createElement("div");
    itemsContainer.className = "am-market-items";
    container.appendChild(itemsContainer);
    renderMarketItems(itemsContainer, eventTicker);
}
/**
 * Render just the market rows for the expanded event into `itemsContainer`,
 * applying the active category chip + text filters. Called on first render and
 * again (alone) when a category chip is tapped — see renderMarketCategoryRow.
 */
function renderMarketItems(itemsContainer, eventTicker) {
    const isMultiSelect = !!document.getElementById("am-step-4");
    itemsContainer.innerHTML = "";
    const marketsToRender = isMultiSelect
        ? getFilteredMarkets(modalState.expandedMarkets)
        : modalState.expandedMarkets;
    for (const market of marketsToRender) {
        if (market.status === "finalized")
            continue;
        const marketEl = document.createElement("div");
        marketEl.className = "am-market-item";
        const displayTitle = getDisplayTitle(market);
        if (isMultiSelect) {
            marketEl.className = "am-market-item am-market-checkbox-item";
            const isSelected = modalState.selectedMarkets.some((m) => m.ticker === market.ticker);
            const label = document.createElement("label");
            label.className = "am-market-checkbox-label";
            const checkbox = document.createElement("input");
            checkbox.type = "checkbox";
            checkbox.className = "am-market-select-cb";
            checkbox.checked = isSelected;
            checkbox.addEventListener("change", () => {
                if (checkbox.checked) {
                    if (!modalState.selectedMarkets.some((m) => m.ticker === market.ticker)) {
                        modalState.selectedMarkets.push(market);
                        modalState.selectedEventTickers.set(market.ticker, eventTicker);
                    }
                }
                else {
                    modalState.selectedMarkets =
                        modalState.selectedMarkets.filter((m) => m.ticker !== market.ticker);
                    modalState.selectedEventTickers.delete(market.ticker);
                }
                updateMarketNextButton();
            });
            const textSpan = document.createElement("span");
            textSpan.className = "am-market-title";
            textSpan.textContent = displayTitle;
            label.appendChild(checkbox);
            label.appendChild(textSpan);
            marketEl.appendChild(label);
            const subtitle = market.yes_sub_title || market.subtitle || "";
            if (subtitle) {
                const subtitleSpan = document.createElement("span");
                subtitleSpan.className = "am-market-subtitle";
                subtitleSpan.textContent = subtitle;
                marketEl.appendChild(subtitleSpan);
            }
        }
        else {
            // Legacy mode: click to select single market
            marketEl.innerHTML = `
				<span class="am-market-title">${escapeHtml(displayTitle)}</span>
				<span class="am-market-subtitle">${escapeHtml(market.yes_sub_title || market.subtitle || "")}</span>
			`;
            marketEl.addEventListener("click", () => {
                modalState.selectedMarket = market;
                modalState.selectedEventTicker = eventTicker;
                advanceToStep2();
            });
        }
        itemsContainer.appendChild(marketEl);
    }
    if (itemsContainer.querySelectorAll(".am-market-item").length === 0) {
        const hint = document.createElement("p");
        hint.className = "am-hint";
        hint.textContent =
            modalState.marketFilterTerm || modalState.marketCategoryFilter
                ? "No markets match your filter."
                : "No active markets found.";
        itemsContainer.appendChild(hint);
    }
}
function advanceToStep2() {
    showStep(2);
    modalState.pendingAssignmentConfig = null;
    const market = modalState.selectedMarket;
    if (!market)
        return;
    // Populate step 2 header with selected market info
    const marketInfo = document.getElementById("am-selected-market-info");
    if (marketInfo) {
        marketInfo.textContent = getDisplayTitle(market);
    }
    // Reset step 2 form
    const sideSelect = document.getElementById("am-side-select");
    const dollarInput = document.getElementById("am-dollar-input");
    const priceInput = document.getElementById("am-price-input");
    if (sideSelect)
        sideSelect.value = "yes";
    labelSideSelect([market]);
    if (dollarInput)
        dollarInput.value = String((0, state_1.getState)().tradingSettings.dollarAmount);
    if (priceInput)
        priceInput.value = String(DEFAULT_ASSIGNMENT_MANUAL_PRICE);
    // Rebuild the button grid for the active layout, nothing pre-checked
    (0, additional_markets_button_grid_1.renderButtonGrid)(new Set());
}
// ---------------------------------------------------------------------------
// Step 3 → Step 4: Multi-select advance to configure
// ---------------------------------------------------------------------------
function advanceToStep4() {
    showStep(4);
    modalState.pendingAssignmentConfig = null;
    // Populate step 4 header with selected markets info
    const marketInfo = document.getElementById("am-selected-market-info");
    if (marketInfo) {
        if (modalState.selectedMarkets.length > 0) {
            marketInfo.innerHTML = modalState.selectedMarkets
                .map((m) => {
                const displayTitle = m.title && m.title.length < 50
                    ? m.title
                    : m.ticker || m.title;
                return `<div>• ${escapeHtml(displayTitle)}</div>`;
            })
                .join("");
        }
        else {
            marketInfo.textContent = "—";
        }
    }
    // Reset step 4 form
    const sideSelect = document.getElementById("am-side-select");
    const dollarInput = document.getElementById("am-dollar-input");
    const priceInput = document.getElementById("am-price-input");
    if (sideSelect)
        sideSelect.value = "yes";
    labelSideSelect(modalState.selectedMarkets);
    if (dollarInput)
        // Default to $1000 or the tier's per-order cap, whichever is lower.
        dollarInput.value = String((0, permissions_1.clampBetToCap)(DEFAULT_ASSIGNMENT_DOLLAR_AMOUNT));
    if (priceInput)
        priceInput.value = String(DEFAULT_ASSIGNMENT_MANUAL_PRICE);
    // Rebuild the button grid for the active layout, pre-selecting the
    // preset-source defaults if applicable
    const defaultButtons = modalState.source
        ? SOURCE_DEFAULT_BUTTONS[modalState.source]
        : undefined;
    (0, additional_markets_button_grid_1.renderButtonGrid)(defaultButtons ?? new Set());
}
function updateMarketNextButton() {
    const nextBtn = document.getElementById("am-market-next-btn");
    if (!nextBtn)
        return;
    const count = modalState.selectedMarkets.length;
    nextBtn.textContent = `Next (${count} selected) →`;
    nextBtn.disabled = count === 0;
}
// ---------------------------------------------------------------------------
// Step 2: Preset rendering
// ---------------------------------------------------------------------------
function renderKalshiPresets() {
    const container = document.getElementById("am-preset-grid");
    if (!container)
        return;
    container.innerHTML = "";
    // Presets follow the primary market's league (e.g. an NBA primary shows NBA
    // series); an unresolved league falls back to a per-league game/period set.
    // Grouped so each league's shared prefix shows once as a header and buttons
    // carry only the market-type suffix. See markets/kalshi-series-presets.ts.
    const groups = (0, kalshi_series_presets_1.getKalshiSeriesPresetGroups)((0, pm_us_default_league_1.resolvePrimaryMarketKalshiLeaguePrefix)());
    for (const group of groups) {
        const header = document.createElement("div");
        header.className = "am-preset-group-header";
        header.textContent = group.label;
        const prefixTag = document.createElement("span");
        prefixTag.className = "am-preset-group-prefix";
        prefixTag.textContent = group.prefix;
        header.appendChild(prefixTag);
        container.appendChild(header);
        for (const preset of group.presets) {
            const btn = document.createElement("button");
            btn.className = "am-preset-btn";
            btn.textContent = preset.suffix;
            // Full ticker drives the search; the label is prefix-stripped.
            btn.dataset.ticker = preset.ticker;
            btn.type = "button";
            btn.addEventListener("click", () => {
                modalState.selectedMarkets = [];
                modalState.selectedEventTickers = new Map();
                updateMarketNextButton();
                showStep(3);
                searchEvents(preset.ticker);
            });
            container.appendChild(btn);
        }
    }
}
/** Render the step-2 sport preset buttons from an already-fetched list. */
function renderPmUsPresets(sports, error) {
    const container = document.getElementById("am-preset-grid");
    if (!container)
        return;
    if (error) {
        container.innerHTML =
            '<p class="am-hint am-error">Failed to load sports</p>';
        return;
    }
    if (sports.length === 0) {
        container.innerHTML = '<p class="am-hint">No sports available</p>';
        return;
    }
    container.innerHTML = "";
    for (const s of sports) {
        const btn = document.createElement("button");
        btn.className = "am-preset-btn";
        btn.textContent = String(s.sport ?? s.name ?? s.slug ?? "Unknown");
        btn.type = "button";
        const slug = (0, pm_us_adapters_1.pmUsSportEntrySlug)(s);
        btn.addEventListener("click", () => {
            modalState.selectedMarkets = [];
            modalState.selectedEventTickers = new Map();
            updateMarketNextButton();
            showStep(3);
            searchPmUsEvents(slug);
        });
        container.appendChild(btn);
    }
}
/**
 * Enter the PM-US branch from the step-1 venue button. Fetches the sport list
 * once, then either skips straight to step 3 on the league the primary market
 * implies (when that league is actually offered by PM-US) or falls back to the
 * full step-2 sport list.
 */
async function enterPmUsVenue() {
    modalState.venue = "pm-us";
    modalState.selectedMarkets = [];
    modalState.selectedEventTickers = new Map();
    updateMarketNextButton();
    const step2Input = document.getElementById("am-step2-search-input");
    if (step2Input) {
        step2Input.placeholder = "Or type a sport slug...";
        step2Input.value = "";
    }
    // Show step 2 immediately so the fetch has somewhere to land; we may jump
    // past it to step 3 once the default league resolves.
    showStep(2);
    const presetGrid = document.getElementById("am-preset-grid");
    if (presetGrid)
        presetGrid.innerHTML = '<p class="am-hint">Loading sports...</p>';
    const { sports, error } = await loadPmUsSportsList();
    // Always populate step 2's preset grid, independent of whether we then
    // skip past it — otherwise step 3's Back button lands on the "Loading
    // sports..." placeholder forever, since nothing else ever replaces it.
    renderPmUsPresets(sports, error);
    // Skip the picker only when the primary market maps to a league PM-US
    // actually offers; otherwise leave step 2 showing the full list just
    // rendered above (validated fallback).
    const slug = (0, pm_us_default_league_1.resolvePrimaryMarketPmUsSlug)();
    if (slug && sports.some((s) => (0, pm_us_adapters_1.pmUsSportEntrySlug)(s) === slug)) {
        showStep(3);
        searchPmUsEvents(slug);
    }
}
// ---------------------------------------------------------------------------
// Step 2 → Confirmation
// ---------------------------------------------------------------------------
function collectStep2Data() {
    const sideSelect = document.getElementById("am-side-select");
    const dollarInput = document.getElementById("am-dollar-input");
    const priceInput = document.getElementById("am-price-input");
    const side = (sideSelect?.value === "no" ? "no" : "yes");
    // Clamp per-assignment size to the tier's per-order cap — this dollar
    // amount becomes its own order (order-placement.ts), so it's an order
    // entry point like the primary one. Server enforces regardless.
    const dollarAmount = (0, permissions_1.clampBetToCap)(Math.max(1, parseInt(dollarInput?.value ?? String(DEFAULT_ASSIGNMENT_DOLLAR_AMOUNT), 10) || DEFAULT_ASSIGNMENT_DOLLAR_AMOUNT));
    const manualPrice = Math.max(1, Math.min(99, parseInt(priceInput?.value ?? String(DEFAULT_ASSIGNMENT_MANUAL_PRICE), 10) || DEFAULT_ASSIGNMENT_MANUAL_PRICE));
    const buttons = [];
    document
        .querySelectorAll(".am-button-checkbox:checked")
        .forEach((cb) => {
        const btnId = cb.dataset.buttonId;
        if (btnId)
            buttons.push(btnId);
    });
    // Zero buttons is allowed: the market is staged (parked) in the active set
    // now and wired up to buttons later via the settings-list edit flow. A
    // parked assignment never fires — order-placement only matches assignments
    // whose buttons include the pressed button — and shows an "Unassigned" row
    // in the settings tooltip until buttons are attached.
    return { side, dollarAmount, manualPrice, buttons };
}
/**
 * Step 4's primary action: create the assignment in one press.
 *
 * There is no confirmation step. The screen it used to advance to restated
 * venue, markets, side, amount, price and buttons — every one of which is
 * still visible and still editable directly above this button, so it asked
 * the operator to re-approve inputs they had just entered. Assignments
 * remain editable and removable from the settings list, so this is not a
 * one-way door. Every step-4 input is clamped/defaulted (and buttons are
 * optional — a market can be parked with none), so there is nothing left to
 * reject here; collectStep2Data always returns a usable configuration.
 */
function submitAssignment() {
    const data = collectStep2Data();
    if (!data)
        return;
    modalState.pendingAssignmentConfig = data;
    confirmAssignment();
}
function confirmAssignment() {
    const data = modalState.pendingAssignmentConfig;
    if (!data) {
        console.warn("[AdditionalMarkets] Confirm aborted: no pending config");
        return;
    }
    const isMulti = modalState.selectedMarkets.length > 0;
    try {
        if (modalState.editingAssignmentId) {
            // Edit mode: update the existing assignment in-place
            (0, additional_markets_service_1.updateAssignment)(modalState.editingAssignmentId, {
                side: data.side,
                dollarAmount: data.dollarAmount,
                manualPrice: data.manualPrice,
                buttons: data.buttons,
            });
        }
        else if (isMulti) {
            for (const market of modalState.selectedMarkets) {
                const eventTicker = modalState.selectedEventTickers.get(market.ticker) ?? "";
                (0, additional_markets_service_1.addAssignment)({
                    marketTicker: market.ticker,
                    marketTitle: market.title,
                    eventTicker,
                    side: data.side,
                    dollarAmount: data.dollarAmount,
                    manualPrice: data.manualPrice,
                    buttons: data.buttons,
                    venue: modalState.venue,
                    source: modalState.source,
                    ...(market.novigOutcomeIds ? { novigOutcomeIds: market.novigOutcomeIds } : {}),
                });
            }
        }
        else {
            const market = modalState.selectedMarket;
            const eventTicker = modalState.selectedEventTicker;
            if (!market || !eventTicker) {
                console.warn("[AdditionalMarkets] Confirm aborted: missing market/eventTicker");
                return;
            }
            (0, additional_markets_service_1.addAssignment)({
                marketTicker: market.ticker,
                marketTitle: market.title,
                eventTicker,
                side: data.side,
                dollarAmount: data.dollarAmount,
                manualPrice: data.manualPrice,
                buttons: data.buttons,
                venue: modalState.venue,
                source: modalState.source,
                ...(market.novigOutcomeIds ? { novigOutcomeIds: market.novigOutcomeIds } : {}),
            });
        }
        // Update UI badges
        (0, additional_markets_ui_1.updateAdditionalMarketBadges)();
        (0, additional_markets_ui_1.updateSettingsInfoAdditionalMarkets)();
    }
    catch (err) {
        console.error("[AdditionalMarkets] Error during confirm:", err);
    }
    finally {
        hideAdditionalMarketsModal();
    }
}
// ---------------------------------------------------------------------------
// Event wiring
// ---------------------------------------------------------------------------
/**
 * Wire up all event listeners for the additional markets modal.
 * Called once from `setupOrderPage`.
 */
function setupAdditionalMarketsModal() {
    // Register the edit callback so tooltip rows can open the edit modal
    (0, additional_markets_ui_1.setEditAssignmentCallback)(openEditModal);
    // Open button (in market-selection modal header)
    const openBtn = document.getElementById("open-additional-markets-btn");
    if (openBtn) {
        openBtn.addEventListener("click", () => {
            showAdditionalMarketsModal();
        });
    }
    // Close / overlay
    const closeBtn = document.getElementById("close-additional-markets-modal");
    const overlay = document.querySelector(".additional-markets-modal-overlay");
    closeBtn?.addEventListener("click", () => hideAdditionalMarketsModal());
    overlay?.addEventListener("click", (e) => {
        if (e.target === overlay)
            hideAdditionalMarketsModal();
    });
    // -----------------------------------------------------------------------
    // Legacy venue selector (used by test DOM)
    // -----------------------------------------------------------------------
    const venueSelect = document.getElementById("am-venue-select");
    venueSelect?.addEventListener("change", () => {
        modalState.venue = (venueSelect.value === "pm-us" ? "pm-us" : "kalshi");
        modalState.events = [];
        modalState.expandedEventTicker = null;
        modalState.expandedMarkets = [];
        const resultsContainer = document.getElementById("am-event-results");
        if (resultsContainer) {
            resultsContainer.innerHTML =
                modalState.venue === "pm-us"
                    ? '<p class="am-hint">Select a sport and press Search.</p>'
                    : '<p class="am-hint">Enter a series ticker to search events.</p>';
        }
        applyVenueUI();
    });
    // Legacy Kalshi series search
    const seriesInput = document.getElementById("am-series-input");
    const searchBtn = document.getElementById("am-search-btn");
    const filterInput = document.getElementById("am-filter-input");
    searchBtn?.addEventListener("click", () => {
        const series = seriesInput?.value.trim() ?? "";
        searchEvents(series);
    });
    seriesInput?.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
            const series = seriesInput?.value.trim() ?? "";
            searchEvents(series);
        }
    });
    seriesInput?.addEventListener("input", () => {
        if (seriesInput) {
            seriesInput.value = seriesInput.value.toUpperCase();
        }
    });
    // Legacy PM-US sport search
    const pmusSearchBtn = document.getElementById("am-pmus-search-btn");
    const sportSelect = document.getElementById("am-sport-select");
    pmusSearchBtn?.addEventListener("click", () => {
        const tagSlug = sportSelect?.value ?? "";
        searchPmUsEvents(tagSlug);
    });
    // Filter within loaded events (shared by both legacy and new DOM)
    filterInput?.addEventListener("input", () => {
        const container = document.getElementById("am-event-results");
        if (!container)
            return;
        renderEventList(container);
    });
    // -----------------------------------------------------------------------
    // Legacy step navigation (used by test DOM)
    // -----------------------------------------------------------------------
    const backToStep1Btn = document.getElementById("am-back-to-step1");
    backToStep1Btn?.addEventListener("click", () => showStep(1));
    const toConfirmBtn = document.getElementById("am-to-confirm-btn");
    toConfirmBtn?.addEventListener("click", () => submitAssignment());
    const backToStep2Btn = document.getElementById("am-back-to-step2");
    backToStep2Btn?.addEventListener("click", () => showStep(2));
    // -----------------------------------------------------------------------
    // New 5-step flow bindings (production DOM)
    // -----------------------------------------------------------------------
    // Step 1: Venue buttons
    const kalshiVenueBtn = document.getElementById("am-venue-btn-kalshi");
    kalshiVenueBtn?.addEventListener("click", () => {
        modalState.venue = "kalshi";
        modalState.selectedMarkets = [];
        modalState.selectedEventTickers = new Map();
        updateMarketNextButton();
        renderKalshiPresets();
        const step2Input = document.getElementById("am-step2-search-input");
        if (step2Input) {
            step2Input.placeholder = "Or type a series ticker...";
            step2Input.value = "";
        }
        showStep(2);
    });
    const pmusVenueBtn = document.getElementById("am-venue-btn-pmus");
    pmusVenueBtn?.addEventListener("click", () => {
        void enterPmUsVenue();
    });
    document.getElementById("am-venue-btn-novig")?.addEventListener("click", () => {
        void enterNovigVenue();
    });
    // Step 2: Search input (manual series/sport entry)
    const step2SearchInput = document.getElementById("am-step2-search-input");
    step2SearchInput?.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
            const value = step2SearchInput.value.trim();
            if (!value)
                return;
            modalState.selectedMarkets = [];
            modalState.selectedEventTickers = new Map();
            updateMarketNextButton();
            showStep(3);
            if (modalState.venue === "pm-us") {
                searchPmUsEvents(value);
            }
            else if (modalState.venue === "novig") {
                void searchNovigEvents(value.toUpperCase());
            }
            else {
                searchEvents(value);
            }
        }
    });
    step2SearchInput?.addEventListener("input", () => {
        if (step2SearchInput && modalState.venue === "kalshi") {
            step2SearchInput.value = step2SearchInput.value.toUpperCase();
        }
    });
    // Step 3: Next button (multi-select → configure)
    const marketNextBtn = document.getElementById("am-market-next-btn");
    marketNextBtn?.addEventListener("click", () => advanceToStep4());
    // Step 4: Dollar ±100 buttons
    const dollarMinus = document.getElementById("am-dollar-minus");
    const dollarPlus = document.getElementById("am-dollar-plus");
    const dollarInput = document.getElementById("am-dollar-input");
    dollarMinus?.addEventListener("click", () => {
        if (dollarInput) {
            const current = parseInt(dollarInput.value, 10) || 0;
            dollarInput.value = String(Math.max(1, current - 1000));
        }
    });
    dollarPlus?.addEventListener("click", () => {
        if (dollarInput) {
            const current = parseInt(dollarInput.value, 10) || 0;
            dollarInput.value = String(Math.min(100000, current + 1000));
        }
    });
    // Step 4: Back to step 3
    const backToStep3Btn = document.getElementById("am-back-to-step3");
    backToStep3Btn?.addEventListener("click", () => showStep(3));
    // Clear all button (far right of each sport's factor row)
    for (const btnId of additional_markets_ui_1.CLEAR_ADDITIONAL_MARKETS_BTN_IDS) {
        document.getElementById(btnId)?.addEventListener("click", () => {
            (0, additional_markets_service_1.clearAllAssignments)();
            (0, additional_markets_ui_1.updateAdditionalMarketBadges)();
            (0, additional_markets_ui_1.updateSettingsInfoAdditionalMarkets)();
        });
    }
    // Bat button – opens modal in bat mode
    const batBtn = document.getElementById("bat-btn");
    batBtn?.addEventListener("click", () => openBatModal());
    // Pitch button – opens modal in pitch mode
    const pitchBtn = document.getElementById("pitch-btn");
    pitchBtn?.addEventListener("click", () => openPitchModal());
    // Total button – opens modal in total mode
    const totalBtn = document.getElementById("total-btn");
    totalBtn?.addEventListener("click", () => openTotalModal());
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
}
